/* ============================================================================
   PANEL DE DESCARGA DE MAPAS

   Para volar sin cobertura. El service worker cachea lo que ya se visitó; esto
   descarga lo que todavía no.

   ══════════════════════════════════════════════════════════════════════════
   LO QUE ESTA PANTALLA NO HACE

   **No arranca una descarga sin decir en qué se está metiendo.** Sudamérica
   entera al detalle son 8,7 GB y medio millón de peticiones: un botón que
   arranca eso y falla a la mitad es peor que uno que avisa antes.

   El conteo se muestra ANTES, y cuando no entra se ofrece el zoom que sí.
   ══════════════════════════════════════════════════════════════════════════
   ========================================================================== */

import React, { useMemo, useRef, useState } from "react";

import {
  REGIONES, TOPES, ZOOM,
  evaluarDescarga, direccionesDeMosaicos, progresoDescarga,
} from "../../domain/descargaMapas.js";
import "./descargaMapas.css";
import { claveMapTiler, guardarClaveMapTiler, MAPAS_MAPTILER, origenDeClave } from "./capasBase.js";
import { evaluarArchivo, resumenDeCobertura, DE_DONDE_SACARLO } from "../../domain/mapaArchivo.js";
import { abrirArchivo, archivoActual, cerrarArchivo } from "../../services/mapas/archivoLocal.js";

/** Las capas que se pueden bajar, con su plantilla de dirección. */
/* Cada capa lleva SU nombre de caché.

   Estaba resuelto con un ternario —«osm» o si no, satelital— y eso funciona
   con dos capas y falla en silencio con la tercera: los mosaicos se guardarían
   en el caché de otra capa, el mapa no los encontraría, y la descarga habría
   ocupado espacio para nada sin que nada lo delate.

   El nombre tiene que coincidir con el `cacheName` del service worker en
   `vite.config.js`: si no coincide, el mapa lee de un caché y la descarga
   escribe en otro. */
/* ═══════════════════════════════════════════════════════════════════════════
   LA DESCARGA MASIVA DE MOSAICOS ESTÁ PROHIBIDA EN LOS SERVIDORES GRATUITOS

   La política de uso de OpenStreetMap lo dice sin ambigüedad:

     «Features such as "Download city/country for offline use" or "Save area
      for later" rely on prefetch/bulk downloading and are therefore
      prohibited. Prefetch/offline patterns place disproportionate load on
      community-funded servers and will be blocked without notice.»

   OpenTopoMap tiene la misma restricción. Los servidores los pagan donaciones,
   y bajar 42.296 mosaicos hace que bloqueen la IP del operador **sin aviso**:
   después de eso el mapa no anda ni siquiera navegando normalmente.

   Por eso el panel NO descarga de esos servidores. No es una limitación
   técnica que se pueda sortear con más reintentos: es una regla del proveedor,
   y saltarla deja al operador sin mapa.

   Para volar sin cobertura hay que usar un proveedor que **permita** el uso
   sin conexión. Todos piden una clave y casi todos cobran, pero el plan
   gratuito de varios alcanza para un operador chico.
   ══════════════════════════════════════════════════════════════════════════ */

const CAPAS = [
  {
    clave: "osm",
    nombre: "Mapa base (OpenStreetMap)",
    plantilla: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    cache: "tiles-osm",
    permiteDescarga: false,
    politica: "https://operations.osmfoundation.org/policies/tiles/",
    nota: "Se ve navegando y queda en caché lo que miras. La descarga masiva "
      + "está prohibida por su política de uso: bajarla haría que bloqueen tu IP.",
  },
  {
    clave: "topo",
    nombre: "Topográfico (OpenTopoMap)",
    plantilla: "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png",
    cache: "tiles-opentopomap",
    permiteDescarga: false,
    politica: "https://opentopomap.org/about",
    nota: "Curvas de nivel, muy útil en sierra. Misma restricción que el mapa base.",
  },
  /* MapTiler Cloud NO está entre los orígenes de descarga, y se deja dicho
     para que nadie lo agregue creyendo que la clave lo habilita:

       «Bulk Downloading. Unless otherwise agreed in writing with MapTiler,
        it is prohibited to batch or excessive bulk download of map tiles.»

     Con la clave del operador, MapTiler se USA NAVEGANDO —y lo que se ve queda
     guardado, que eso sí lo permiten— pero no se baja por anticipado. */
  {
    clave: "propio",
    nombre: "Proveedor propio (con clave)",
    /* La plantilla la pone el operador: cada proveedor tiene la suya. */
    plantilla: null,
    cache: "tiles-propio",
    permiteDescarga: true,
    nota: "Un proveedor que permita uso sin conexión: MapTiler, Mapbox, "
      + "Thunderforest, o tu propio servidor de mosaicos. Pega acá la dirección "
      + "con tu clave.",
  },
];

export default function PanelDescargaMapas() {
  const [regionClave, setRegionClave] = useState("peru");
  const [capaClave, setCapaClave] = useState("propio");

  /* La dirección del proveedor propio se recuerda: incluye la clave del
     operador y volver a pegarla en cada vuelo es lo que hace que nadie la
     use. */
  const [plantillaPropia, setPlantillaPropia] = useState(() => {
    try { return localStorage.getItem("hcMosaicosPropios") || ""; } catch { return ""; }
  });

  const guardarPlantilla = (v) => {
    setPlantillaPropia(v);
    try { localStorage.setItem("hcMosaicosPropios", v); } catch { /* modo privado */ }
  };
  const [zoomMax, setZoomMax] = useState(10);
  const [estado, setEstado] = useState(null);

  /* Para poder cancelar: una descarga de veinte minutos sin botón de parar es
     una descarga que el piloto mata cerrando la aplicación, y eso deja el
     caché a medias sin que nadie sepa hasta dónde llegó. */
  const cancelar = useRef(false);

  const region = REGIONES.find((r) => r.clave === regionClave) || REGIONES[0];
  const capa = CAPAS.find((c) => c.clave === capaClave) || CAPAS[0];
  const plantilla = capa.plantilla || plantillaPropia.trim();

  /* Una plantilla sirve solo si trae los tres marcadores: sin {z}/{x}/{y} las
     direcciones salen todas iguales y se descarga el mismo mosaico miles de
     veces. */
  const plantillaValida = /\{z\}/.test(plantilla) && /\{x\}/.test(plantilla) && /\{y\}/.test(plantilla);

  const puedeDescargar = capa.permiteDescarga && plantillaValida;

  const evaluacion = useMemo(
    () => evaluarDescarga(region, ZOOM.minimo, zoomMax),
    [region, zoomMax],
  );

  const descargar = async () => {
    if (!evaluacion.permitida) return;

    cancelar.current = false;
    const desdeMs = Date.now();
    let hechos = 0;
    let fallidos = 0;

    setEstado({ corriendo: true, ...progresoDescarga({ hechos: 0, total: evaluacion.total, desdeMs }) });

    const urls = direccionesDeMosaicos(region, ZOOM.minimo, zoomMax, plantilla);
    const cache = await caches.open(capa.cache);

    /* De a tandas. Más en paralelo satura la red del dispositivo y hace que
       los servidores empiecen a rechazar. */
    let tanda = [];
    const correrTanda = async () => {
      await Promise.all(tanda.map(async (url) => {
        try {
          /* Si ya está en caché no se vuelve a pedir: repetir la descarga de
             una región sería empezar de cero cada vez. */
          const hay = await cache.match(url);
          if (hay) { hechos += 1; return; }

          const res = await fetch(url, { mode: "cors" });
          if (res.ok) { await cache.put(url, res.clone()); hechos += 1; } else { fallidos += 1; }
        } catch {
          fallidos += 1;
        }
      }));
      tanda = [];
      setEstado({
        corriendo: true,
        ...progresoDescarga({ hechos, fallidos, total: evaluacion.total, desdeMs }),
      });
    };

    for (const url of urls) {
      if (cancelar.current) break;
      tanda.push(url);
      if (tanda.length >= TOPES.enParalelo) {
        // eslint-disable-next-line no-await-in-loop
        await correrTanda();
      }
    }
    if (tanda.length > 0 && !cancelar.current) await correrTanda();

    setEstado({
      corriendo: false,
      cancelada: cancelar.current,
      ...progresoDescarga({ hechos, fallidos, total: evaluacion.total, desdeMs }),
    });
  };

  return (
    <section className="dm">
      <header className="dm__cabecera">
        <h3 className="dm__titulo">Mapas para volar sin cobertura</h3>

      {/* ═══ LA CLAVE DEL OPERADOR ═══

          Con ella, MapTiler aparece entre las capas del mapa y **lo que se
          navega queda guardado** — que es lo que sus condiciones permiten.

          Lo que NO se puede es bajar una región por anticipado con esta clave.
          Decirlo acá, junto al campo, evita que alguien la pegue esperando lo
          otro. */}
      {/* ═══ EL CAMINO SIMPLE, PRIMERO ═══

          Un archivo con el mapa adentro: sin cuota, sin clave, sin política
          que violar y sin depender de cobertura. Es lo que de verdad resuelve
          volar donde no hay señal, así que va arriba de todo. */}
      <MapaDeArchivo />

      <ClaveMapTiler />
      </header>

      <p className="dm__nota">
        El mapa se guarda en el dispositivo. Lo que no se descargue va a quedar
        en blanco donde no haya señal.
      </p>

      <div className="dm__campos">
        <label className="dm__campo">
          <span>Región</span>
          <select value={regionClave} onChange={(e) => setRegionClave(e.target.value)}>
            {REGIONES.map((r) => <option key={r.clave} value={r.clave}>{r.nombre}</option>)}
          </select>
        </label>

        <label className="dm__campo">
          <span>Capa</span>
          <select value={capaClave} onChange={(e) => setCapaClave(e.target.value)}>
            {CAPAS.map((c) => <option key={c.clave} value={c.clave}>{c.nombre}</option>)}
          </select>
          <small>{capa.nota}</small>
        </label>

        <label className="dm__campo">
          <span>Detalle (zoom hasta {zoomMax})</span>
          <input
            type="range"
            min={7}
            max={ZOOM.maximo}
            value={zoomMax}
            onChange={(e) => setZoomMax(Number(e.target.value))}
          />
          <small>
            {zoomMax <= 9 && "Poblados y rutas principales."}
            {zoomMax >= 10 && zoomMax <= 11 && "Calles y caminos."}
            {zoomMax >= 12 && "Detalle fino. Ocupa mucho más."}
          </small>
        </label>
      </div>

      {/* EL CONTEO ANTES DE DESCARGAR. */}
      <div className={`dm__cuenta${evaluacion.permitida ? "" : " es-alto"}`}>
        {evaluacion.valido ? (
          <>
            <div className="dm__cifra">
              <span>Mosaicos</span>
              <strong>{evaluacion.total.toLocaleString()}</strong>
            </div>
            <div className="dm__cifra">
              <span>Espacio</span>
              <strong>{evaluacion.mb} MB</strong>
            </div>
          </>
        ) : (
          <p className="dm__motivo">{evaluacion.motivo}</p>
        )}
      </div>

      {!evaluacion.permitida && evaluacion.motivo && evaluacion.valido && (
        <p className="dm__motivo" role="alert">{evaluacion.motivo}</p>
      )}

      {evaluacion.avisos?.map((a) => <p className="dm__aviso" key={a}>{a}</p>)}

      {/* LA POLÍTICA DEL PROVEEDOR, antes del botón.

          No es un detalle legal: bajar de un servidor que lo prohíbe hace que
          bloqueen la IP del operador SIN AVISO, y después el mapa no anda ni
          navegando normalmente. */}
      {!capa.permiteDescarga && (
        <div className="dm__prohibido" role="alert">
          <strong>Este mapa no se puede descargar.</strong>
          <p>{capa.nota}</p>
          <p>
            Para volar sin cobertura necesitas un proveedor que permita el uso
            sin conexión. Elige <strong>Proveedor propio</strong> y pega la
            dirección con tu clave.
          </p>
          {capa.politica && (
            <a href={capa.politica} target="_blank" rel="noreferrer">Ver su política de uso</a>
          )}
        </div>
      )}

      {capa.clave === "propio" && (
        <label className="dm__campo dm__campo--ancho">
          <span>Dirección de los mosaicos</span>
          <input
            value={plantillaPropia}
            onChange={(e) => guardarPlantilla(e.target.value)}
            placeholder="https://api.tuproveedor.com/{z}/{x}/{y}.png?key=TU_CLAVE"
            spellCheck={false}
          />
          <small>
            {plantillaPropia.trim() === ""
              ? "MapTiler, Mapbox, Thunderforest o tu propio servidor. Casi todos tienen plan gratuito."
              : (plantillaValida
                ? "Se guarda en este dispositivo junto con tu clave."
                : "Faltan los marcadores {z}, {x} y {y}: sin ellos se descargaría el mismo mosaico miles de veces.")}
          </small>
        </label>
      )}

      {estado?.corriendo ? (
        <div className="dm__progreso">
          <div className="dm__barra">
            <div className="dm__barra-llena" style={{ width: `${estado.porcentaje}%` }} />
          </div>
          <p className="dm__nota">
            {estado.hechos.toLocaleString()} de {estado.total.toLocaleString()}
            {estado.restanteSegundos !== null && ` · quedan ${Math.ceil(estado.restanteSegundos / 60)} min`}
            {estado.fallidos > 0 && ` · ${estado.fallidos} sin bajar`}
          </p>
          {/* Muchos fallos seguidos significan que se perdió la red: seguir
              gastando batería contra un servidor que rechaza no sirve. */}
          {estado.demasiadosFallos && (
            <p className="dm__motivo" role="alert">
              Más de la mitad está fallando. Revisa la conexión: lo descargado
              hasta acá queda guardado.
            </p>
          )}
          <button type="button" className="dm__cancelar" onClick={() => { cancelar.current = true; }}>
            Cancelar
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="dm__descargar"
          onClick={descargar}
          disabled={!evaluacion.permitida || !puedeDescargar}
        >
          Descargar {region.nombre}
        </button>
      )}

      {estado && !estado.corriendo && (
        <p className="dm__nota">
          {estado.cancelada ? "Cancelada. " : "Listo. "}
          {estado.hechos.toLocaleString()} mosaicos guardados
          {estado.fallidos > 0 && `, ${estado.fallidos} no se pudieron bajar`}.
          {/* Lo descargado sirve aunque falte parte: un mapa con huecos es
              mejor que ninguno. */}
          {estado.fallidos > 0 && " Lo que se bajó ya está disponible sin señal."}
        </p>
      )}
    </section>
  );
}


/* --------------------------------------------------------- clave MapTiler */

/**
 * La clave de MapTiler del operador.
 *
 * Se guarda en el dispositivo. **No se sincroniza ni se exporta**: es una
 * credencial de pago y meterla en el paquete de exportación la dejaría en
 * cualquier copia del libro de vuelo.
 */
function ClaveMapTiler() {
  /* Solo lo que el piloto pegó: la del operador viaja en la aplicación y no se
     muestra en el campo, para que nadie la borre creyendo que está vacío. */
  const [clave, setClave] = useState(() => {
    try { return (localStorage.getItem("hcClaveMapTiler") || "").trim(); } catch { return ""; }
  });
  const [verla, setVerla] = useState(false);

  void hayClave;
  const origen = origenDeClave();
  const hayClave = Boolean(claveMapTiler());

  const cambiar = (v) => {
    setClave(v);
    guardarClaveMapTiler(v);
  };

  return (
    <div className="dm__clave">
      <label className="dm__campo dm__campo--ancho">
        <span>Clave de MapTiler</span>
        <input
          type={verla ? "text" : "password"}
          value={clave}
          onChange={(e) => cambiar(e.target.value)}
          placeholder="Pega tu clave, o la dirección entera con ?key=…"
          spellCheck={false}
          autoComplete="off"
        />
        <small>
          {/* De dónde sale la clave que se está usando. Sin esto, un piloto
              con el campo vacío no sabe si tiene mapa o no. */}
          {origen === "propia" && `Usando TU clave. En el mapa hay ${MAPAS_MAPTILER.length} capas de MapTiler.`}
          {origen === "operador" && "Usando la clave del operador, que viene con la aplicación. "
            + "Solo pega una acá si tienes cuenta propia: tus mosaicos los pagarías tú "
            + "y no saldrían de la cuota del operador."}
          {!origen && "Sin clave no aparecen las capas de MapTiler. "
            + "Pega la tuya de cloud.maptiler.com, o pedile al operador que la incluya en la aplicación."}
        </small>
      </label>

      {clave.trim() && (
        <>
          <button type="button" className="dm__ver" onClick={() => setVerla((v) => !v)}>
            {verla ? "Ocultar" : "Ver"}
          </button>
          {/* Quitar la propia vuelve a la del operador, si la hay. */}
          <button type="button" className="dm__ver" onClick={() => cambiar("")}>
            Quitar
          </button>
        </>
      )}

      {/* La distinción que evita la confusión de siempre. */}
      <p className="dm__nota dm__nota--ancha">
        <strong>Lo que MapTiler permite:</strong> navegar, y que lo que veas
        quede guardado en el dispositivo. Volando dos veces por la misma zona,
        la segunda no pide nada a la red.
        {" "}<strong>Lo que no permite:</strong> descargar una región entera por
        anticipado. Para eso hace falta MapTiler Data —su producto de mosaicos
        autohospedados— o un permiso por escrito.
      </p>
    </div>
  );
}


/* ------------------------------------------------------- mapa en archivo */

/**
 * Cargar un archivo de mapa.
 *
 * Es el camino que funciona sin cobertura: un `.pmtiles` con la región
 * adentro. No hay descarga, ni cuota, ni clave.
 */
function MapaDeArchivo() {
  const [cargado, setCargado] = useState(archivoActual);
  const [mensaje, setMensaje] = useState(null);
  const [cargando, setCargando] = useState(false);

  const elegir = async (e) => {
    const archivo = e.target.files?.[0];
    if (!archivo) return;

    const evaluacion = evaluarArchivo(archivo);
    if (!evaluacion.sirve) {
      setMensaje({ error: true, texto: evaluacion.motivo });
      return;
    }

    setCargando(true);
    setMensaje(null);

    const r = await abrirArchivo(archivo);
    setCargando(false);

    if (!r.ok) {
      setMensaje({ error: true, texto: r.motivo });
      return;
    }

    setCargado(archivoActual());
    setMensaje({
      error: false,
      texto: `Listo. ${evaluacion.mb} MB de mapa, disponible sin señal.`,
    });
  };

  const cobertura = cargado ? resumenDeCobertura(cargado.cabecera) : null;

  return (
    <div className="dm__archivo">
      <h4 className="dm__sub">Mapa en un archivo · lo más simple</h4>

      <p className="dm__nota">
        <strong>Para qué sirve:</strong> volando donde no hay señal de celular,
        el mapa de internet no carga y la pantalla queda en blanco. Este archivo
        tiene el mapa <em>adentro del iPad</em>, así que se ve igual sin señal.
        {" "}El GPS sigue andando porque es satelital: lo único que faltaba era
        el mapa.
      </p>
      <p className="dm__nota">
        Se baja UNA VEZ desde una computadora, se pasa al iPad, y se elige acá.
      </p>

      <label className="dm__elegir">
        <input type="file" accept=".pmtiles" onChange={elegir} />
        <span>{cargando ? "Abriendo…" : (cargado ? "Cambiar archivo" : "Elegir archivo de mapa")}</span>
      </label>

      {cargado && cobertura && (
        <div className="dm__cargado">
          <strong>{cargado.nombre}</strong>
          <p className="dm__nota">
            Cubre de {cobertura.norte.toFixed(1)}° a {cobertura.sur.toFixed(1)}° de latitud
            y de {cobertura.oeste.toFixed(1)}° a {cobertura.este.toFixed(1)}° de longitud.
            {cobertura.detalle && ` ${cobertura.detalle}`}
          </p>
          <button type="button" className="dm__ver" onClick={() => { cerrarArchivo(); setCargado(null); }}>
            Quitar
          </button>
        </div>
      )}

      {mensaje && (
        <p className={mensaje.error ? "dm__error" : "dm__ok"} role={mensaje.error ? "alert" : "status"}>
          {mensaje.texto}
        </p>
      )}

      {/* La limitación que hay que decir, no esconder. */}
      {cargado && (
        <p className="dm__nota dm__nota--ancha">
          <strong>Antes de cada vuelo hay que volver a elegirlo.</strong> El
          navegador no puede quedarse con el permiso al archivo entre sesiones.
          Hacelo en tierra, con el iPad ya cargado.
        </p>
      )}

      <details className="dm__donde">
        <summary>De dónde sacar el archivo</summary>
        <ul>
          {DE_DONDE_SACARLO.map((d) => (
            <li key={d.nombre}>
              <a href={d.url} target="_blank" rel="noreferrer">{d.nombre}</a>
              {d.pago ? " (de pago)" : " (gratis)"} — {d.nota}
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
}
